import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import SimpleMdeReact from "react-simplemde-editor";
import Select from "react-select";
import Breadcrumbs from "../../../../common-elements/breadcrumbs/Breadcrumbs";
import {
  ComposeMessage,
  EnterYourMessages,
  Subject,
  Send,
} from "../../../../utils/Constant";
import {
  Container,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
} from "reactstrap";
import { Image, H5 } from "../../../../AbstractElements";
import { dynamicImage } from "../../../../service";

const roleOptions = [
  { value: "sales", label: "Sales" },
  { value: "developers", label: "Developers" },
  { value: "finance", label: "Finance" },
];

const tagOptions = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const userData: {
  [key: string]: {
    value: string;
    label: string;
    role: string;
    avatar?: string;
  }[];
} = {
  sales: [
    { value: "all-sales", label: "All (Sales)", role: "sales" },
    {
      value: "john",
      label: "John Doe (Sales)",
      role: "sales",
      avatar: "dashboard-8/user3.png",
    },
    {
      value: "jane",
      label: "Jane Smith (Sales)",
      role: "sales",
      avatar: "dashboard-8/user3.png",
    },
  ],
  developers: [
    { value: "all-developers", label: "All (Developers)", role: "developers" },
    {
      value: "alice",
      label: "Alice Johnson (Developers)",
      role: "developers",
      avatar: "dashboard-8/user3.png",
    },
    {
      value: "bob",
      label: "Bob Wilson (Developers)",
      role: "developers",
      avatar: "dashboard-8/user3.png",
    },
  ],
  finance: [
    { value: "all-finance", label: "All (Finance)", role: "finance" },
    {
      value: "emma",
      label: "Emma Brown (Finance)",
      role: "finance",
      avatar: "dashboard-8/user3.png",
    },
    {
      value: "mike",
      label: "Mike Davis (Finance)",
      role: "finance",
      avatar: "dashboard-8/user3.png",
    },
  ],
};

interface AddNewEmail {
  userEmail: string;
  subject: string;
  message: string;
  tags: string;
}

interface ComposeEmailPageProps {
  userProfile?: {
    avatar: string;
    name: string;
    role: string;
  };
}

const ComposeEmailPage: React.FC<ComposeEmailPageProps> = ({ userProfile }) => {
  const [selectedRoles, setSelectedRoles] = useState<typeof roleOptions>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [selectedTag, setSelectedTag] = useState<(typeof tagOptions)[0] | null>(
    null
  );
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<AddNewEmail>();

  const onSubmit: SubmitHandler<AddNewEmail> = (data) => {
    if (data.userEmail) {
      console.log("Email data:", data);
      reset();
      setValue("message", "");
      setValue("tags", "");
      setSelectedRoles([]);
      setSelectedUsers([]);
      setSelectedTag(null);
      navigate(`${process.env.PUBLIC_URL}/communication/email`);
    }
  };

  const handleRoleChange = (selectedOptions: any) => {
    setSelectedRoles(selectedOptions || []);
    setSelectedUsers([]);
    setValue("userEmail", "");
  };

  const handleUserChange = (selectedOptions: any) => {
    let newSelectedUsers = selectedOptions || [];

    const selectedValues = newSelectedUsers.map((opt: any) => opt.value);

    newSelectedUsers = newSelectedUsers.filter((user: any) => {
      if (user.value.startsWith("all-")) {
        return true;
      }
      const roleOfUser = user.role;
      const allOptionForRole = `all-${roleOfUser}`;
      return !selectedValues.includes(allOptionForRole);
    });

    const finalSelection = newSelectedUsers.filter((user: any) => {
      if (!user.value.startsWith("all-")) {
        return true;
      }
      const roleOfAll = user.role;
      return !newSelectedUsers.some(
        (u: any) => u.role === roleOfAll && !u.value.startsWith("all-")
      );
    });

    setSelectedUsers(finalSelection);

    const emails = finalSelection.map((user: any) => {
      if (user.value.startsWith("all-")) {
        return `${user.role}@example.com`;
      }
      return `${user.value}@example.com`;
    });
    setValue("userEmail", Array.from(new Set(emails)).join(", "));
  };

  const handleTagChange = (selectedOption: any) => {
    const newSelectedTag = selectedOption || null;
    setSelectedTag(newSelectedTag);
    setValue("tags", newSelectedTag ? newSelectedTag.value : "");
  };

  const getSelectableUsers = () => {
    if (!selectedRoles.length) {
      return [];
    }

    const userOptions = selectedRoles.flatMap((role) => userData[role.value]);

    userOptions.sort((a, b) => {
      const aIsAll = a.value.startsWith("all-");
      const bIsAll = b.value.startsWith("all-");

      if (aIsAll && !bIsAll) return -1;
      if (!aIsAll && bIsAll) return 1;
      return a.label.localeCompare(b.label);
    });

    const selectedValues = selectedUsers.map((u) => u.value);

    return userOptions.map((user) => {
      const isAllForRoleSelected = selectedValues.includes(`all-${user.role}`);
      const areIndividualUsersInRoleSelected = selectedValues.some(
        (v) =>
          !v.startsWith("all-") &&
          userData[user.role]?.some((u) => u.value === v)
      );

      let isDisabled = false;
      if (user.value.startsWith("all-")) {
        isDisabled = areIndividualUsersInRoleSelected;
      } else {
        isDisabled = isAllForRoleSelected;
      }

      return {
        ...user,
        isDisabled,
      };
    });
  };

  return (
    <div className="page-body">
      <Breadcrumbs
        mainTitle={ComposeMessage}
        parents={[
          {
            label: "Communication",
            link: `${process.env.PUBLIC_URL}/communication/email`,
          },
          {
            label: "Email",
            link: `${process.env.PUBLIC_URL}/communication/email`,
          },
        ]}
      />
      <Container fluid>
        <Card>
          <CardBody>
            {userProfile && (
              <div className="text-center mb-4">
                <Image
                  className="img-100 rounded-circle"
                  alt={userProfile.name}
                  src={dynamicImage(userProfile.avatar)}
                />
                <H5 className="mt-2">{userProfile.name}</H5>
                <p className="text-muted">{userProfile.role}</p>
              </div>
            )}
            <div className="email-wrap email-main-wrapper">
              <Row>
                <Col sm="12">
                  <div className="compose-email-page">
                    <Form
                      className="form-bookmark needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <FormGroup>
                        <Row>
                          <Col sm="2">
                            <Label className="col-form-label">Role:</Label>
                          </Col>
                          <Col sm="10">
                            <Select
                              options={roleOptions}
                              isMulti
                              isClearable
                              value={selectedRoles}
                              onChange={handleRoleChange}
                              className="basic-multi-select"
                              classNamePrefix="select"
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup>
                        <Row>
                          <Col sm="2">
                            <Label className="col-form-label">To:</Label>
                          </Col>
                          <Col sm="10">
                            <Select
                              isMulti
                              isClearable
                              isDisabled={!selectedRoles.length}
                              options={getSelectableUsers()}
                              value={selectedUsers}
                              onChange={handleUserChange}
                              className="basic-multi-select"
                              classNamePrefix="select"
                              formatOptionLabel={(option: any) => (
                                <div className="d-flex align-items-center">
                                  {option.avatar && (
                                    <Image
                                      src={dynamicImage(option.avatar)}
                                      alt={option.label}
                                      style={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: "50%",
                                        marginRight: 8,
                                      }}
                                    />
                                  )}
                                  <span>{option.label}</span>
                                </div>
                              )}
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup>
                        <Row>
                          <Col sm="2">
                            <Label className="col-form-label">Tags:</Label>
                          </Col>
                          <Col sm="10">
                            <Select
                              options={tagOptions}
                              isClearable
                              value={selectedTag}
                              onChange={handleTagChange}
                              className="basic-multi-select"
                              classNamePrefix="select"
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup>
                        <Row>
                          <Col sm="2">
                            <Label className="col-form-label">{Subject}:</Label>
                          </Col>
                          <Col sm="10">
                            <Input
                              type="text"
                              {...register("subject")}
                              className="form-control"
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <div id="editor">
                        <SimpleMdeReact
                          id="editor_container"
                          placeholder={EnterYourMessages}
                          options={{ autofocus: true, spellChecker: false }}
                          onChange={(value) => setValue("message", value)}
                        />
                      </div>
                      <div className="mt-3 d-flex justify-content-end">
                        <Button
                          color="secondary"
                          type="button"
                          onClick={() => navigate("/communication/email")}
                        >
                          Cancel
                        </Button>
                        <Button color="primary" type="submit" className="ms-2">
                          {Send}
                        </Button>
                      </div>
                    </Form>
                  </div>
                </Col>
              </Row>
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ComposeEmailPage;
